import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ShieldCheck, DollarSign, Clock, Ban, Phone, ExternalLink,
  Download, Loader2, AlertTriangle, CheckCircle2, ChevronLeft,
  Coffee,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface RGSettings {
  daily_deposit_limit: number | null;
  weekly_deposit_limit: number | null;
  monthly_deposit_limit: number | null;
  pending_daily_limit: number | null;
  pending_weekly_limit: number | null;
  pending_monthly_limit: number | null;
  pending_limit_effective_at: string | null;
  self_excluded: boolean;
  self_exclusion_start: string | null;
  self_exclusion_end: string | null;
  self_exclusion_type: string | null;
  cool_off_until: string | null;
  responsible_gaming_acknowledged: boolean;
}

interface Spending {
  daily_deposits: number;
  weekly_deposits: number;
  monthly_deposits: number;
}

interface Transaction {
  id: string;
  type: string;
  amount: number;
  status: string;
  description: string | null;
  created_at: string;
}

interface ResponsibleGamingPageProps {
  onBack?: () => void;
}

export default function ResponsibleGamingPage({ onBack }: ResponsibleGamingPageProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [settings, setSettings] = useState<RGSettings | null>(null);
  const [spending, setSpending] = useState<Spending | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Limit inputs
  const [dailyLimit, setDailyLimit] = useState('');
  const [weeklyLimit, setWeeklyLimit] = useState('');
  const [monthlyLimit, setMonthlyLimit] = useState('');

  // Self-exclusion
  const [exclusionDuration, setExclusionDuration] = useState('');
  const [showExclusionConfirm, setShowExclusionConfirm] = useState(false);
  const [exclusionConfirmed, setExclusionConfirmed] = useState(false);

  const fetchSettings = async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) return;

      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/responsible-gaming`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      if (data.settings) {
        setSettings(data.settings);
        setSpending(data.spending);
        setTransactions(data.transactions || []);
        // Pre-fill limit inputs
        setDailyLimit(data.settings.daily_deposit_limit?.toString() || '');
        setWeeklyLimit(data.settings.weekly_deposit_limit?.toString() || '');
        setMonthlyLimit(data.settings.monthly_deposit_limit?.toString() || '');
      }
    } catch (err) {
      toast({ title: 'Error loading settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, [user]);

  const callAction = async (action: string, body: Record<string, unknown>) => {
    setSaving(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) return;

      const res = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/responsible-gaming`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action, ...body }),
        }
      );
      const data = await res.json();
      if (data.error) {
        toast({ title: 'Error', description: data.error, variant: 'destructive' });
      } else {
        toast({ title: 'Updated', description: data.message });
        if (data.settings) setSettings(data.settings);
        fetchSettings();
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLimits = () => {
    callAction('update_limits', {
      daily: dailyLimit ? Number(dailyLimit) : null,
      weekly: weeklyLimit ? Number(weeklyLimit) : null,
      monthly: monthlyLimit ? Number(monthlyLimit) : null,
    });
  };

  const handleSelfExclude = () => {
    if (!exclusionDuration) return;
    callAction('self_exclude', { duration: exclusionDuration });
    setShowExclusionConfirm(false);
    setExclusionConfirmed(false);
    setExclusionDuration('');
  };

  const handleCoolOff = (hours: number) => {
    callAction('cool_off', { hours });
  };

  const exportCSV = () => {
    const rows = [
      ['Date', 'Type', 'Amount', 'Description'],
      ...transactions.map(t => [
        new Date(t.created_at).toLocaleDateString(),
        t.type,
        `$${Number(t.amount).toFixed(2)}`,
        t.description || '',
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'spending-history.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const isExcluded = settings?.self_excluded && (
    settings.self_exclusion_type === 'permanent' ||
    !settings.self_exclusion_end ||
    new Date(settings.self_exclusion_end) > new Date()
  );

  const isCoolingOff = settings?.cool_off_until && new Date(settings.cool_off_until) > new Date();

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 px-2">
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
        <div>
          <h1 className="text-xl font-display font-bold text-foreground flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Responsible Gaming
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {user ? 'Manage your deposit limits, breaks, and self-exclusion settings' : 'Our commitment to responsible gaming'}
          </p>
        </div>
      </div>

      {/* Static policy content — always visible */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-3 text-sm text-muted-foreground leading-relaxed">
          <p>
            Sabotage Fantasy is committed to providing a safe and responsible gaming environment.
            We believe fantasy sports should be fun, and we provide tools to help you stay in control.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Set daily, weekly, or monthly limits on deposits and card purchases at any time</li>
            <li>Take a cool-off break to pause deposits and purchases</li>
            <li>Self-exclude temporarily or permanently. This cannot be reversed early</li>
            <li>Export your full spending history as CSV</li>
          </ul>
          <p>
            If you or someone you know has a gambling problem, free help is available 24/7.
            Call <strong className="text-foreground">1-800-522-4700</strong> or text HOME to <strong className="text-foreground">741741</strong>.
          </p>
        </CardContent>
      </Card>

      {/* Interactive tools — only for authenticated users */}
      {loading && user && (
        <div className="flex items-center justify-center py-10">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Status banners */}
      {!loading && isExcluded && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4 flex items-start gap-3">
            <Ban className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-400">Self-Excluded</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {settings?.self_exclusion_type === 'permanent'
                  ? 'Your account is permanently self-excluded.'
                  : `Excluded until ${new Date(settings!.self_exclusion_end!).toLocaleDateString()}. This cannot be reversed early.`}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && isCoolingOff && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 flex items-start gap-3">
            <Coffee className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-400">Cool-Off Active</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Deposits and purchases paused until {new Date(settings!.cool_off_until!).toLocaleString()}.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {user && !loading && <>
      {/* A) DEPOSIT LIMITS */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-primary" />
            Deposit &amp; Purchase Limits
          </CardTitle>
          <CardDescription className="text-xs">
            Set limits on how much you can deposit and spend on cards. Lowering a limit takes
            effect immediately. Raising a limit takes effect after 24 hours.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current spending */}
          {spending && (
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2.5 rounded-lg bg-muted/30 border border-border text-center">
                <p className="text-[10px] text-muted-foreground uppercase">Today</p>
                <p className="text-sm font-bold text-foreground">${spending.daily_deposits.toFixed(2)}</p>
                {settings?.daily_deposit_limit && (
                  <p className="text-[9px] text-muted-foreground">of ${Number(settings.daily_deposit_limit).toFixed(0)}</p>
                )}
              </div>
              <div className="p-2.5 rounded-lg bg-muted/30 border border-border text-center">
                <p className="text-[10px] text-muted-foreground uppercase">This Week</p>
                <p className="text-sm font-bold text-foreground">${spending.weekly_deposits.toFixed(2)}</p>
                {settings?.weekly_deposit_limit && (
                  <p className="text-[9px] text-muted-foreground">of ${Number(settings.weekly_deposit_limit).toFixed(0)}</p>
                )}
              </div>
              <div className="p-2.5 rounded-lg bg-muted/30 border border-border text-center">
                <p className="text-[10px] text-muted-foreground uppercase">This Month</p>
                <p className="text-sm font-bold text-foreground">${spending.monthly_deposits.toFixed(2)}</p>
                {settings?.monthly_deposit_limit && (
                  <p className="text-[9px] text-muted-foreground">of ${Number(settings.monthly_deposit_limit).toFixed(0)}</p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Daily Limit ($)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                placeholder="No limit"
                value={dailyLimit}
                onChange={(e) => setDailyLimit(e.target.value)}
                className="bg-muted/30"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Weekly Limit ($)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                placeholder="No limit"
                value={weeklyLimit}
                onChange={(e) => setWeeklyLimit(e.target.value)}
                className="bg-muted/30"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Monthly Limit ($)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                placeholder="No limit"
                value={monthlyLimit}
                onChange={(e) => setMonthlyLimit(e.target.value)}
                className="bg-muted/30"
              />
            </div>
          </div>

          {/* Pending increase notice */}
          {settings?.pending_limit_effective_at && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <Clock className="h-3.5 w-3.5 text-amber-400 shrink-0" />
              <p className="text-xs text-amber-400">
                Limit increase pending, takes effect {new Date(settings.pending_limit_effective_at).toLocaleString()}
              </p>
            </div>
          )}

          <Button
            onClick={handleUpdateLimits}
            disabled={saving}
            className="w-full bg-primary hover:bg-primary/90"
            size="sm"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Update Limits
          </Button>
        </CardContent>
      </Card>

      {/* B) SELF-EXCLUSION */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Ban className="h-4 w-4 text-red-400" />
            Self-Exclusion
          </CardTitle>
          <CardDescription className="text-xs">
            Temporarily or permanently exclude yourself from depositing, purchasing credits, and joining new leagues.
            This cannot be reversed early.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isExcluded ? (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
              <p className="text-sm font-semibold text-red-400">
                {settings?.self_exclusion_type === 'permanent'
                  ? 'Permanently Excluded'
                  : `Excluded until ${new Date(settings!.self_exclusion_end!).toLocaleDateString()}`}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">Contact support if you need assistance.</p>
            </div>
          ) : (
            <>
              <Select value={exclusionDuration} onValueChange={setExclusionDuration}>
                <SelectTrigger><SelectValue placeholder="Select duration..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">24 Hours</SelectItem>
                  <SelectItem value="72h">72 Hours</SelectItem>
                  <SelectItem value="1w">1 Week</SelectItem>
                  <SelectItem value="30d">30 Days</SelectItem>
                  <SelectItem value="6m">6 Months</SelectItem>
                  <SelectItem value="permanent">Permanent</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                disabled={!exclusionDuration || saving}
                onClick={() => setShowExclusionConfirm(true)}
              >
                <Ban className="h-4 w-4 mr-2" />
                Exclude Me
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Self-exclusion confirmation dialog */}
      <Dialog open={showExclusionConfirm} onOpenChange={setShowExclusionConfirm}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Confirm Self-Exclusion
            </DialogTitle>
            <DialogDescription className="text-xs">
              {exclusionDuration === 'permanent'
                ? 'This will permanently exclude your account. This action cannot be undone.'
                : `You will be excluded for ${
                    { '24h': '24 hours', '72h': '72 hours', '1w': '1 week', '30d': '30 days', '6m': '6 months' }[exclusionDuration] || exclusionDuration
                  }. This cannot be reversed early.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={exclusionConfirmed}
                onChange={(e) => setExclusionConfirmed(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-xs text-muted-foreground">
                I understand this cannot be reversed and I will not be able to deposit, purchase credits, or join new leagues during this period.
              </span>
            </label>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                className="flex-1"
                disabled={!exclusionConfirmed || saving}
                onClick={handleSelfExclude}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirm Exclusion
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => { setShowExclusionConfirm(false); setExclusionConfirmed(false); }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* C) COOL-OFF */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Coffee className="h-4 w-4 text-amber-400" />
            Take a Break
          </CardTitle>
          <CardDescription className="text-xs">
            Pause deposits and purchases for a short period. You can still manage your lineups.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {isCoolingOff ? (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
              <p className="text-sm font-semibold text-amber-400">
                Cool-off until {new Date(settings!.cool_off_until!).toLocaleString()}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {[24, 48, 72].map(h => (
                <Button
                  key={h}
                  variant="outline"
                  size="sm"
                  onClick={() => handleCoolOff(h)}
                  disabled={saving}
                  className="border-border text-foreground hover:bg-muted"
                >
                  {h}hr Break
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* D) SPENDING HISTORY */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Spending History
            </CardTitle>
            {transactions.length > 0 && (
              <Button variant="ghost" size="sm" onClick={exportCSV} className="h-7 px-2 text-[10px]">
                <Download className="h-3 w-3 mr-1" /> Export CSV
              </Button>
            )}
          </div>
          <CardDescription className="text-xs">Last 30 days of deposits and purchases</CardDescription>
        </CardHeader>
        <CardContent className="space-y-0">
          {transactions.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No transactions in the last 30 days</p>
          ) : (
            <div className="max-h-60 overflow-y-auto divide-y divide-border/30">
              {transactions.map(t => (
                <div key={t.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm text-foreground capitalize">{t.type.replace(/_/g, ' ')}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {new Date(t.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge className={cn(
                    'text-[10px] border',
                    t.type === 'deposit'
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                      : 'bg-primary/10 text-primary border-primary/20'
                  )}>
                    ${Number(t.amount).toFixed(2)}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      </>}

      {/* E) RESOURCES */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="h-4 w-4 text-emerald-400" />
            Help & Resources
          </CardTitle>
          <CardDescription className="text-xs">
            If you or someone you know has a gambling problem, help is available.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ResourceLink
            name="National Problem Gambling Helpline"
            phone="1-800-522-4700"
            description="Free, confidential, 24/7"
            url="https://www.ncpgambling.org"
          />
          <ResourceLink
            name="Crisis Text Line"
            phone="Text HOME to 741741"
            description="Free crisis counseling via text"
            url="https://www.crisistextline.org"
          />
          <ResourceLink
            name="Gamblers Anonymous"
            description="Find meetings and support"
            url="https://www.gamblersanonymous.org"
          />
          <ResourceLink
            name="National Council on Problem Gambling"
            description="Education, advocacy, and resources"
            url="https://www.ncpgambling.org"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function ResourceLink({ name, phone, description, url }: {
  name: string;
  phone?: string;
  description: string;
  url: string;
}) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-3 p-3 rounded-lg bg-muted/20 border border-border hover:border-primary/30 transition-colors"
    >
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{name}</p>
        {phone && <p className="text-xs text-primary font-medium">{phone}</p>}
        <p className="text-[10px] text-muted-foreground">{description}</p>
      </div>
    </a>
  );
}
